/**
 * API client with JWT authentication
 */

// Use environment variable if set, otherwise use current origin (for production)
export const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Resolve an asset URL returned by the backend.
 * - If the URL is null/empty → returns null.
 * - If the URL starts with `/api/` → prepends the backend origin (served by FastAPI).
 * - If the URL is already absolute (http://, https://) → returned as-is.
 * - Otherwise (e.g. `/branding/logo_light.png`) → treated as frontend-relative
 *   and returned as-is (served by the React static build).
 *
 * This ensures branding assets ALWAYS load in production, even when the backend
 * disk-uploaded files are missing (ephemeral container storage) — the frontend
 * bundle ships its own default copies under `/public/branding/`.
 */
export const resolveAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/api/')) return getBackendUrl() + url;
  return url;
};

const API_BASE_URL = getBackendUrl() + '/api';

/**
 * Get stored auth token
 */
const getAuthToken = () => {
  return sessionStorage.getItem('token');
};

/**
 * Make authenticated API request
 */
export const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers = {
    ...options.headers,
  };
  
  // Add auth header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add Content-Type for JSON requests
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      body: options.body instanceof FormData 
        ? options.body 
        : options.body 
          ? JSON.stringify(options.body)
          : undefined,
    });
    
    // Handle 401 - redirect to login
    if (response.status === 401) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.location.href = '/admin/login';
      throw new Error('Unauthorized');
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Convenience methods
 */
export const api = {
  get: (endpoint, options = {}) => 
    apiRequest(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, body, options = {}) => 
    apiRequest(endpoint, { ...options, method: 'POST', body }),
  
  put: (endpoint, body, options = {}) => 
    apiRequest(endpoint, { ...options, method: 'PUT', body }),
  
  patch: (endpoint, body, options = {}) => 
    apiRequest(endpoint, { ...options, method: 'PATCH', body }),
  
  delete: (endpoint, body, options = {}) => 
    apiRequest(endpoint, { ...options, method: 'DELETE', body }),
};

export default api;
